import React, { useCallback } from 'react';
import { Box } from '@amzn/awsui-components-core';
import { OrgChartTreeProps, OrgChartEmployee } from '../../types/orgChart.types';
import { EmployeeCardNode } from './EmployeeCardNode';
import './OrgChartTree.scss';

interface TreeNodeProps {
  login: string;
  employees: Map<string, OrgChartEmployee>;
  expandedNodes: Set<string>;
  selectedEmployees: Set<string>;
  leaderLogin: string;
  depth: number;
  onExpand: (login: string) => void;
  onCollapse: (login: string) => void;
  onToggleSelection: (login: string) => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  login,
  employees,
  expandedNodes,
  selectedEmployees,
  leaderLogin,
  depth,
  onExpand,
  onCollapse,
  onToggleSelection
}) => {
  const employee = employees.get(login);

  if (!employee) {
    return null;
  }

  const isExpanded = expandedNodes.has(login);
  const isLeader = login === leaderLogin;

  // Find children by looking for employees whose managerLogin matches this employee's login
  const visibleDirectReports = Array.from(employees.values())
    .filter((emp) => emp.managerLogin === login && !emp.isManuallyAdded)
    .map((emp) => emp.login);

  return (
    <div className="org-chart-tree__node">
      <EmployeeCardNode
        employee={employee}
        isExpanded={isExpanded}
        isLeader={isLeader}
        isSelected={selectedEmployees.has(login)}
        depth={depth}
        hasChildren={visibleDirectReports.length > 0}
        onExpand={() => onExpand(login)}
        onCollapse={() => onCollapse(login)}
        onToggleSelection={() => onToggleSelection(login)}
      />

      {isExpanded && visibleDirectReports.length > 0 && (
        <div className="org-chart-tree__children">
          {visibleDirectReports.map((reportLogin) => (
            <TreeNode
              key={reportLogin}
              login={reportLogin}
              employees={employees}
              expandedNodes={expandedNodes}
              selectedEmployees={selectedEmployees}
              leaderLogin={leaderLogin}
              depth={depth + 1}
              onExpand={onExpand}
              onCollapse={onCollapse}
              onToggleSelection={onToggleSelection}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const OrgChartTree: React.FC<OrgChartTreeProps> = ({
  leader,
  employees,
  expandedNodes,
  selectedEmployees,
  onExpand,
  onCollapse,
  onToggleSelection
}) => {
  const manuallyAddedEmployees = Array.from(employees.values()).filter((emp) => emp.isManuallyAdded && emp.login !== leader.login);

  const handleExpand = useCallback(
    (login: string) => {
      onExpand(login);
    },
    [onExpand]
  );

  const handleCollapse = useCallback(
    (login: string) => {
      onCollapse(login);
    },
    [onCollapse]
  );

  const handleToggleSelection = useCallback(
    (login: string) => {
      onToggleSelection(login);
    },
    [onToggleSelection]
  );

  return (
    <div className="org-chart-tree">
      <div className="org-chart-tree__content">
        {/* Display leader as root node */}
        <TreeNode
          key={leader.login}
          login={leader.login}
          employees={employees}
          expandedNodes={expandedNodes}
          selectedEmployees={selectedEmployees}
          leaderLogin={leader.login}
          depth={0}
          onExpand={handleExpand}
          onCollapse={handleCollapse}
          onToggleSelection={handleToggleSelection}
        />

        {manuallyAddedEmployees.length > 0 && (
          <div className="org-chart-tree__manually-added">
            <Box variant="small" color="text-body-secondary" padding={{ top: 'm', bottom: 'xs' }}>
              Manually Added Employees ({manuallyAddedEmployees.length})
            </Box>
            <div className="org-chart-tree__manually-added-list">
              {manuallyAddedEmployees.map((employee) => (
                <EmployeeCardNode
                  key={employee.login}
                  employee={employee}
                  isExpanded={false}
                  isLeader={employee.login === leader.login}
                  isSelected={selectedEmployees.has(employee.login)}
                  depth={0}
                  hasChildren={false}
                  onExpand={() => {}}
                  onCollapse={() => {}}
                  onToggleSelection={() => handleToggleSelection(employee.login)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgChartTree;
